// ⚠️ PERFORM CORE — Ne pas modifier sans validation complète
// Connecté à : chat_messages
// Utilisé dans : Dashboard, CentralBrainDock
import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, X } from "lucide-react";
import { openBrainChat } from "@/components/brain/ChatContainer";
import { MicrophoneButton } from "@/components/ui/microphone-button";
import { useSpeechToText } from "@/hooks/useSpeechToText";
import { useCycleTracking } from "@/hooks/useCycleTracking";
import { buildCycleContext } from "@/lib/cycleContext";
import { isEnabled } from "@/constants/featureFlags";

const STORAGE_KEY = "central-brain-history";

const PLACEHOLDERS = [
  "Une question ?",
  "Dis-moi tout…",
  "Besoin d'un conseil ?",
  "Parle-moi…",
  "Tape 'performe' pour ton état du jour…",
];

const PERFORME_DASHBOARD_PROMPT = `[PERFORM DASHBOARD REQUEST]
L'utilisateur veut un résumé rapide de son état actuel. Réponds avec un dashboard compact en français :

1. **Statut profil** : profil complet ou lacunes à combler (quels champs manquent ?)
2. **Aujourd'hui** : séance prévue ce jour ? check-in fait ou pas ?
3. **Prochains jours** : 2-3 prochaines séances planifiées (jour + nom)
4. **Alerte ou conseil** : 1 seul, le plus utile en ce moment

Format : court, scannable, sans blabla. Pas plus de 8 lignes au total.
Si l'utilisateur n'a pas encore configuré son profil ou son programme, guide-le vers la prochaine étape prioritaire.`;

function getPlaceholder() {
  return PLACEHOLDERS[Math.floor(Math.random() * PLACEHOLDERS.length)];
}

function hasBrainMessages(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
}

let globalToggleFreeInput: (() => void) | null = null;
export function toggleBrainFreeInput() {
  globalToggleFreeInput?.();
}

export default function BrainFreeInput() {
  const [open, setOpen] = useState(false);
  const [hasMessages, setHasMessages] = useState(hasBrainMessages);
  const [value, setValue] = useState("");
  const [placeholder] = useState(getPlaceholder);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { isListening, transcript, startListening, stopListening, resetTranscript } = useSpeechToText();
  const { cycleData, currentPhase } = useCycleTracking();

  useEffect(() => {
    if (transcript) {
      setValue((prev) => prev + transcript);
      resetTranscript();
    }
  }, [transcript, resetTranscript]);

  useEffect(() => {
    const check = () => setHasMessages(hasBrainMessages());
    window.addEventListener("storage", check);
    window.addEventListener("brain-history-changed", check);
    const interval = setInterval(check, 2000);
    return () => {
      window.removeEventListener("storage", check);
      window.removeEventListener("brain-history-changed", check);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    globalToggleFreeInput = () => setOpen((p) => !p);
    return () => { globalToggleFreeInput = null; };
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  const handleSubmit = useCallback(() => {
    const text = value.trim();
    if (!text) return;

    // "performe" keyword → silent dashboard prompt injection
    let messageToSend = text;
    if (text.toLowerCase().replace(/\s+/g, "") === "performe") {
      messageToSend = PERFORME_DASHBOARD_PROMPT;
    }

    // Weekly recap keyword → silent structured weekly review prompt injection
    const WEEKLY_TRIGGERS = [
      "semaine dernière", "la semaine passée", "cette semaine", "semaine prochaine",
      "semaine pro", "j'ai fait", "j'ai mangé", "j'ai dormi", "j'ai raté",
      "j'ai sauté", "j'ai mal", "je prévois", "je veux faire", "mon bilan",
    ];

    const isWeeklyRecap = WEEKLY_TRIGGERS.some(t =>
      text.toLowerCase().includes(t)
    );

    if (isWeeklyRecap) {
      const weekContext = `[MODE BILAN HEBDOMADAIRE]
L'utilisateur parle de sa semaine. Analyse sa description et adapte ta réponse en 3 parties :
1. **Bilan** : ce que tu comprends de sa semaine (résumé en 2 lignes)
2. **Adaptation** : 1-2 ajustements concrets pour le programme de la semaine suivante (charge, récup, nutrition) basés sur ce qu'il dit
3. **Action** : une seule action prioritaire pour demain

Format : court, actionnable, sans blabla. Si des infos manquent, pose UNE seule question ciblée.
Message utilisateur : ${text}`;
      messageToSend = weekContext;
    }

    // Silent cycle context injection — invisible to the user, sent to the Brain
    try {
      if (
        isEnabled("FEMALE_HEALTH") &&
        cycleData?.tracking_enabled &&
        cycleData?.last_period_start &&
        currentPhase
      ) {
        const ctx = buildCycleContext(currentPhase);
        if (ctx) messageToSend = `${ctx}\n\n${messageToSend}`;
      }
    } catch {
      // keep messageToSend as-is on cycle context failure
    }

    setValue("");
    setOpen(false);
    openBrainChat();
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("brain-inject-prompt", { detail: { prompt: messageToSend } })
      );
    }, 200);
  }, [value, cycleData, currentPhase]);

  if (hasMessages) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <div className="flex justify-center px-4 py-2">
            <div className="flex items-center gap-2 w-full max-w-sm rounded-full border border-border/50 bg-card/90 backdrop-blur-sm shadow-perform-ai pl-4 pr-1.5 py-1.5 border-l-2 border-l-[hsl(var(--perform-ai))]">
              <textarea
                ref={inputRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                placeholder={placeholder}
                rows={1}
                className="flex-1 bg-transparent text-base text-ai text-foreground placeholder:text-muted-foreground/50 focus:outline-none resize-none leading-relaxed min-h-[1.5rem] max-h-20"
              />
              <MicrophoneButton
                isListening={isListening}
                onStartListening={startListening}
                onStopListening={stopListening}
                compact
              />
              <button
                onClick={handleSubmit}
                disabled={!value.trim()}
                className="shrink-0 flex items-center justify-center h-7 w-7 rounded-full transition-all bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed"
              >
                <Send className="h-3 w-3" />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="shrink-0 flex items-center justify-center h-7 w-7 rounded-full hover:bg-muted transition-colors"
              >
                <X className="h-3 w-3 text-muted-foreground" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
